import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Audio } from "expo-av";
import { useFonts } from "expo-font";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Animated,
  Dimensions,
  FlatList,
  Pressable,
  SafeAreaView,
  Text,
  TouchableOpacity,
  View
} from "react-native";
import TouchableScale from "react-native-touchable-scale";
import TypeWriter from "react-native-typewriter";
import Button from "./components/Button";
import puzzleWords from "./puzzleWords.json"; // for testing
import styles from "./styles";

const [width, height] = [
  Dimensions.get("window").width,
  Dimensions.get("window").height,
];

interface ILetter {
  letter: string;
  index: number;
  repetitionIndex: number;
}

const App: React.FC = (props) => {
  const ref = useRef();
  const animation = useRef(null);
  const [word, setWord] = useState<string>("");
  const [letters, setLetters] = useState<ILetter[]>([]);
  const [selectedLetters, setSelectedLetters] = useState<ILetter[]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number>(30);
  const [showAlert, setAlert] = useState("");
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [extraChars, setExtraChars] = useState(0);
  const [previousWords, setPreviousWords] = useState<string[]>([]);
  const [userSubmissions, setUserSubmissions] = useState("");
  const [score, setScore] = useState(0);
  const [retry, setRetry] = useState(3);
  const [allWords, setAllWords] = useState(puzzleWords);
  const [showLottie, setShowLottie] = useState(false);
  const [showWrongLottie, setShowWrongLottie] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [theme, setTheme] = useState({});
  const [loading, setLoading] = useState(false);
  const [correctWords, setCorrectWords] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [correctAnswers, setCorrectAnswers] = React.useState(0);
  const [totalTimeTaken, setTotalTimeTaken] = useState(0); // The total time taken across all rounds
  const [viewScore, setViewScore] = useState(false);
  const [bonus, setBonus] = useState(false);
  const [hideLetters, setHideLetters] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const [fontsLoaded] = useFonts({
    'Baloo': require("./assets/fonts/Baloo-Regular.ttf"),
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);
  useEffect(() => {
    onLayoutRootView();
  }, [onLayoutRootView]);

  const winSound = async () => {
    const { sound } = await Audio.Sound.createAsync(
      require("./assets/sounds/win.mp3")
    );
    await sound.playAsync();
  };

  const wrongSound = async () => {
    const { sound } = await Audio.Sound.createAsync(
      require("./assets/sounds/wrong.mp3")
    );
    await sound.playAsync();
  };

  const bonusSound = async () => {
    const { sound } = await Audio.Sound.createAsync(
      require("./assets/sounds/bonus.mp3")
    );
    await sound.playAsync();
  };

  const endGameSound = async () => {
    const { sound } = await Audio.Sound.createAsync(
      require("./assets/sounds/success.mp3")
    );
    await sound.playAsync();
  };

  const startGame = (puzzleWords) => {
    setAllWords(puzzleWords);
    setRounds([]);
    selectNewWord(puzzleWords);
    setScore(0);
    setExtraChars(0);
    setSecondsLeft(30);
    setRetry(3);
    setGameStarted(true);
    setShowInstructions(false);
    setLoading(false);
  };

  const selectNewWord = (wordsArray = allWords) => {
    if (wordsArray && wordsArray.length > 0) {
      const selectedWord =
        wordsArray[Math.floor(Math.random() * wordsArray.length)];
      setWord(selectedWord);
      setTimeout(() => {
        setAlert("Hint: " + selectedWord.length + " letters");
      }, 1000);
      // Calculate repetition index for each character
      const charOccurrences = {};
      const chars = selectedWord.split("").map((char, index) => {
        charOccurrences[char] = (charOccurrences[char] || 0) + 1;
        return {
          letter: char,
          index,
          repetitionIndex: charOccurrences[char],
        };
      });

      for (let i = 0; i < extraChars; i++) {
        let newChar;
        do {
          newChar = String.fromCharCode(
            Math.floor(Math.random() * (90 - 65 + 1)) + 65
          ).toLowerCase(); // Convert to lowercase
        } while (selectedWord.includes(newChar));
        if (letters.length < 16) {
          chars.push({
            letter: newChar,
            index: selectedWord.length + i,
          });
        }
      }

      shuffleLetters();
      setLetters(chars);
      setSelectedLetters([]);
    }
  };

  const selectLetter = (selectedLetter: ILetter) => {
    // console.log(selectedLetter);
    setSelectedLetters((letters) => [...letters, selectedLetter]);
    shuffleLetters();
  };

  const shuffleLetters = () => {
    const shuffledLetters = [...letters];
    for (let i = shuffledLetters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledLetters[i], shuffledLetters[j]] = [
        shuffledLetters[j],
        shuffledLetters[i],
      ];
    }
    setLetters(shuffledLetters);
  };

  const getRandomColor = () => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    const a = Math.random() * 0.5 + 0.5; // For semi-transparent colors. Adjust as needed.
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const AnimatedItem = ({ item }) => {
    const fadeAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
      const delay = Math.floor(Math.random() * 700) + 200; // Random number between 200 and 900

      Animated.timing(fadeAnim, {
        toValue: 0.3,
        delay, // Apply the random delay
        duration: 400,
        useNativeDriver: false,
      }).start();
    }, []);

    return (
      <Animated.View
        style={{
          backgroundColor: secondsLeft > 6 ? getRandomColor() : "#f01d71",
          padding: "2%",
          margin: "1%",
          borderRadius: 12,
          justifyContent: "center",
          alignItems: "center",
          opacity: fadeAnim,
        }}
      >
        <Text
          style={{
            fontSize: 21,
            color: "white",
            textAlign: "center",
            fontWeight: "900",
            fontFamily: "Baloo",
          }}
        >
          {item}
        </Text>
      </Animated.View>
    );
  };

  const renderItem = ({ item }) => <AnimatedItem item={item} />;

  let stepRepeat = [...allWords, ...allWords];

  // Fisher-Yates shuffle
  for (let i = stepRepeat.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [stepRepeat[i], stepRepeat[j]] = [stepRepeat[j], stepRepeat[i]];
  }

  let date = new Date();
  let formattedDate = date.toString();

  const playSound = async () => {
    // console.log("Loading Sound");
    const { sound } = await Audio.Sound.createAsync(
      require("./assets/sounds/letter.m4a")
    );
    // console.log("Playing Sound", sound);
    await sound.playAsync();
  };

  useEffect(() => {}, [showLottie, showWrongLottie, loading]);

  useEffect(() => {
    if (retry === 0) {
      setGameOver(true);
      endGameSound();
      setGameStarted(false);
    }
  }, [retry]);

  useEffect(() => {
    let userSubmission = selectedLetters
      .map((letter) => letter.letter)
      .join("");
    shuffleLetters();
    setPreviousWords([...previousWords, word]);
    setUserSubmissions(userSubmission);
    if (selectedLetters.length === word.length) {
      if (selectedLetters.every((letter, index) => letter.index === index)) {
        winSound();
        setShowLottie(true);
        setTimeout(() => {
          setAlert("Correct!");
        }, 1000);
        animation.current?.play();
        let userSubmission = selectedLetters
          .map((letter) => letter.letter)
          .join("");
        setRounds((prevRounds) => [...prevRounds, [word, userSubmission]]);
        setTotalTimeTaken((prevTotalTime) => prevTotalTime + secondsLeft);
        setCorrectWords([...correctWords, word]);
        setTimeout(() => {
          setExtraChars(extraChars + 1);
          setShowLottie(false);
          setSecondsLeft(30);
          setScore(gameStarted ? score + 1 + secondsLeft : 0); // add time left to score here
          selectNewWord();
          setAlert("");
          // selectedLetters.length = 0;
        }, 1000);
      } else {
        wrongSound();
        setShowWrongLottie(true);
        setAlert("The word was: " + word);
        setRounds((prevRounds) => [...prevRounds, [word, userSubmission]]);
        setTotalTimeTaken((prevTotalTime) => prevTotalTime + secondsLeft);
        setTimeout(() => {
          selectedLetters.length = 0;
          setRetry(retry - 1);
          selectNewWord();
          setShowWrongLottie(false);
          setAlert("");
        }, 1500);
      }
    }
  }, [selectedLetters]);

  useEffect(() => {
    if (secondsLeft > 0 && gameStarted) {
      const timer = setTimeout(() => {
        setSecondsLeft(secondsLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (secondsLeft === 0 && gameStarted) {
      setAlert(`Time is up. The correct word was: ${word}`);
      let userSubmission = selectedLetters
        .map((letter) => letter.letter)
        .join("");
      setRounds((prevRounds) => [...prevRounds, [word, userSubmission]]);
      setShowWrongLottie(true);
      setRetry(retry - 1);
      if (retry > 0) {
        setSecondsLeft(30);
        setTimeout(() => {
          setShowWrongLottie(false);
          selectNewWord();
        }, 1000);
        setTimeout(() => {
          setAlert("");
          selectedLetters.length = 0;
        }, 2000);
      }
    }
  }, [secondsLeft, gameStarted]);

  React.useEffect(() => {
    const correct = rounds.filter((item) => item[0] === item[1]).length;
    setCorrectAnswers(correct);
  }, [rounds]);

  return fontsLoaded && (
    <View style={styles.container}>
      <View
        style={{
          width: '100%',
          maxWidth: 540,
          height: height,
          overflow: "hidden",
          justifyContent: "center",
          alignItems: "center",
          alignContent: "flex-end",
        }}
      >
        <StatusBar style="light" />
        <SafeAreaView />

        <View style={styles.header}>
          <View style={styles.pill}>
            <Text style={styles.scoreText}>🌟 {score}</Text>
          </View>
          <View style={styles.pill}>
            <Text
              style={[
                styles.secondsLeft,
                {
                  color:
                    secondsLeft > 20
                      ? "limegreen"
                      : secondsLeft > 10
                      ? "gold"
                      : secondsLeft > 5
                      ? "orange"
                      : "red",
                },
              ]}
            >
              {secondsLeft}
            </Text>
          </View>
          <View style={styles.pill}>
            <Text style={styles.scoreText}>❤️ {retry}</Text>
          </View>
        </View>

        {showInstructions && (
          <>
            <View>
              <TypeWriter
                typing={1}
                minDelay={15}
                style={[
                  styles.introText,
                  {
                    marginTop: "12%",
                  },
                ]}
              >
                Hello. Welcome to
              </TypeWriter>
              <Text style={styles.title}>
                Spellexia
                
              </Text>
            </View>

            <Text
              style={[
                styles.introText,
                {
                  fontSize: 16,
                  paddingBottom: 12,
                },
              ]}
            >
              Play the demo
            </Text>
          </>
        )}

        <View style={styles.selectedContainer}>
          {selectedLetters.length > 0 && (
            <View style={styles.letterBox}>
              <Text style={styles.submissionText}>{userSubmissions}</Text>
              <MaterialCommunityIcons
                onPress={() => {
                  setSelectedLetters(selectedLetters.slice(0, -1));
                }}
                name="chevron-left"
                size={32}
                color="white"
                style={{ marginLeft: 12, marginRight: -12 }}
              />
              <MaterialCommunityIcons
                onPress={() => {
                  setSelectedLetters([]);
                }}
                name="close"
                size={32}
                color="white"
                style={{ marginLeft: 12, marginRight: -12 }}
              />
            </View>
          )}
          {gameStarted ? (
            <View style={styles.cardContainer}>

{showLottie && (
              <Text style={{
                fontSize: 100,
                textAlign: "center",
              }}>🤩</Text>
            )}
            {showWrongLottie && (
             <Text style={{
              fontSize: 100,
              textAlign: "center",
            }}>😖</Text>
            )}
              <Text style={styles.introText}>{showAlert}</Text>

              {!hideLetters && (
                <>
                  {retry > 0 && (
                    <FlatList
                    bounces={false}
                    data={letters}
                    numColumns={4}
                    renderItem={({ item }) => (
                      <TouchableScale
                        tension={700}
                        activeScale={0.4}
                        onPress={() => {
                          playSound();
                          selectLetter(item);
                        }}
                      >
                        <View
                          style={{
                            ...styles.letterContainer,
                            backgroundColor: selectedLetters.some(
                              (selected) =>
                                selected.letter === item.letter &&
                                selected.repetitionIndex ===
                                  item.repetitionIndex
                            )
                              ? "plum"
                              : "#fff",
                          }}
                        >
                          {item.repetitionIndex > 1 && (
                            <View style={styles.repetitionIndex}>
                              <Text
                                style={{
                                  fontSize: 16,
                                  textAlign: "center",
                                  fontFamily: "Baloo",
                                  color: "white",
                                }}
                              >
                                {item.repetitionIndex}
                              </Text>
                            </View>
                          )}
                          <Text
                            style={{
                              fontSize: 32,
                              textAlign: "center",
                              textTransform: "capitalize",
                              fontFamily: "Baloo",
                              color: "black",
                            }}
                          >
                            {item.letter}
                          </Text>
                        </View>
                      </TouchableScale>
                    )}
                    keyExtractor={(item) => item.index.toString()}
                    style={{
                      padding: 24,
                      paddingBottom: 80,
                      marginLeft: -24,
                      marginRight: -24,
                      marginBottom: -96,
                    }}
                  />
                  )}
                </>
              )}
            </View>
          ) : (
            <>
              {allWords.length > 1 ? (
                <>
                  {gameOver && (
                    <View
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Text
                        style={[
                          styles.title,
                          {
                            color: "gold",
                            marginBottom: 0,
                            fontSize: 48,
                            letterSpacing: -1,
                          },
                        ]}
                      >
                        Game Over
                      </Text>
                      <Pressable
                        style={{
                          backgroundColor: "#333",
                          borderRadius: 12,
                          paddingVertical: 4,
                          paddingHorizontal: 12,
                          marginTop: -10,
                          marginBottom: 12,
                        }}
                        onPress={() => setViewScore(!viewScore)}
                      >
                        <Text
                          style={{
                            color: "white",
                            fontSize: 24,
                            fontWeight: "800",
                            fontFamily: "Baloo",
                          }}
                        >
                          {!viewScore ? "View Results" : "View Score"}
                        </Text>
                      </Pressable>
                      {!viewScore ? (
                        <TouchableOpacity>
                          <View
                            style={[
                              {
                                paddingHorizontal: 24,
                                paddingBottom: 12,
                                backgroundColor: "black",
                                borderRadius: 19,
                                borderColor: "gold",
                                borderWidth: 8,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.gameCardText,
                                {
                                  marginBottom: -12,
                                  paddingTop: 12,
                                  fontSize: 24,
                                },
                              ]}
                            >
                              🏆 My Spellexia Score
                            </Text>
                            <Text
                              style={{
                                fontSize: 72,
                                color: "gold",
                                fontWeight: "900",
                                paddingVertical: 12,
                              }}
                            >
                              {score}
                              <Text
                                style={{
                                  fontSize: 64,
                                }}
                              >
                                🌟
                              </Text>
                            </Text>

                            <Text style={styles.gameCardText}>
                              🏁 Answers: {correctAnswers}/{rounds.length}
                            </Text>
                            <Text style={styles.gameCardText}>
                              ⏱️ Time: {totalTimeTaken} seconds
                            </Text>

                            <View
                              style={{
                                flex: 1,
                                backgroundColor: "transparent",
                              }}
                            />
                          </View>
                        </TouchableOpacity>
                      ) : (
                        <View
                          style={{
                            height: height / 3,
                            width: 400,
                          }}
                        >
                          <View style={{ flex: 1 }} />
                          <FlatList
                            data={rounds}
                            stickyHeaderIndices={[0]}
                            ListHeaderComponent={() => (
                              <View
                                style={{
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                  paddingVertical: 6,
                                  backgroundColor: "rgba(0,0,0,0.9)",
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 18,
                                    color: "gold",
                                    fontWeight: "800",
                                    fontFamily: "Baloo",
                                    textAlign: "left",
                                    textTransform: "uppercase",
                                    paddingHorizontal: 12,
                                    paddingBottom: 3,
                                  }}
                                >
                                  Puzzle
                                </Text>
                                <View style={{ flex: 1 }} />
                                <Text
                                  style={{
                                    fontSize: 18,
                                    color: "gold",
                                    fontWeight: "800",
                                    fontFamily: "Baloo",
                                    textAlign: "right",
                                    textTransform: "uppercase",
                                    paddingHorizontal: 12,
                                    paddingBottom: 3,
                                  }}
                                >
                                  Answer
                                </Text>
                              </View>
                            )}
                            renderItem={({ item }) => (
                              <View
                                style={{
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                  paddingVertical: 6,
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 19,
                                    color: "white",
                                    fontWeight: "800",
                                    fontFamily: "Baloo",
                                    textAlign: "left",
                                    textTransform: "uppercase",
                                    paddingHorizontal: 12,
                                  }}
                                >
                                  {item[0]}
                                </Text>
                                <View style={{ flex: 1 }} />
                                <Text
                                  style={{
                                    fontSize: 19,
                                    color:
                                      item[0] === item[1]
                                        ? "green"
                                        : item[1] !== ""
                                        ? "#f01d71"
                                        : "gray",
                                    fontWeight: "800",
                                    fontFamily: "Baloo",
                                    textAlign: "right",
                                    textTransform: "uppercase",
                                    paddingHorizontal: 12,
                                  }}
                                >
                                  {item[1] === "" ? "Time out" : item[1]}
                                </Text>
                              </View>
                            )}
                            keyExtractor={(item, index) => index.toString()}
                            numColumns={1}
                            style={{
                              backgroundColor: "rgba(0,0,0,0.5)",
                              borderRadius: 19,
                              borderColor: "black",
                              borderWidth: 12,
                              marginBottom: 12,
                            }}
                            bounces={false}
                            ListFooterComponent={() => (
                              <View style={{ height: 24 }} />
                            )}
                          />
                        </View>
                      )}
                    </View>
                  )}
                  <>
                    {!loading ? (
                      <>
                        <Button
                          text="Start Game"
                          onPress={() => {
                            setLoading(true);
                            startGame(puzzleWords);
                          }}
                        />
                      </>
                    ) : (
                      <Text style={styles.loadingText}>Loading game..</Text>
                    )}
                  </>
                </>
              ) : (
                <View
                  style={{
                    alignItems: "center",
                  }}
                >
                  {theme && !loading ? (
                    <Button
                      text="Start game"
                      onPress={() => {
                        setLoading(true);
                        startGame(puzzleWords);
                      }}
                    />
                  ) : (
                    <Text style={styles.loadingText}>Loading game..</Text>
                  )}
                </View>
              )}
            </>
          )}
        </View>

        {loading && (
          <Text style={styles.hint}>
            Hint: A pink letter is the second time the letter is used in a word,
            blue is the third.
          </Text>
        )}
        <SafeAreaView />
      </View>
    </View>
  )
};

export default App;
